import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTools from "@/components/feature/ScrollTools";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubmitForm from "@/components/feature/SubmitForm";
import { useSeo } from "@/hooks/useSeo";
import { brand, homeStats, principles, testimonials } from "@/mocks/site";

export default function About() {
  useSeo({
    title: "关于我们｜瓜田黑料的内容原则与联系方式",
    description:
      "了解瓜田黑料是谁：一个不做第一现场、只做信息第二现场的舆论反转观察站。这里介绍我们的站点定位、内容原则、证据标注方式、读者口碑与线索投递联系方式。",
    keywords: "关于瓜田黑料,内容原则,舆论反转观察站,线索投递,联系方式",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田黑料",
        url: "https://stranding.cn/about",
        inLanguage: "zh-CN",
        description: "介绍瓜田黑料的站点定位、内容原则、证据标注方式与联系方式。",
        mainEntity: {
          "@type": "Organization",
          name: brand.name,
          alternateName: brand.suffix,
          email: brand.email,
          url: "https://stranding.cn/",
          description: brand.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://stranding.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://stranding.cn/about" },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">ABOUT US</p>
                <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  我们只做信息的第二现场
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{brand.intro}</p>
              </div>
              <div className="lg:col-span-5">
                <dl className="grid grid-cols-2 gap-4 border-l border-background-300 pl-6 md:grid-cols-4 lg:grid-cols-2">
                  {homeStats.slice(0, 2).map((stat) => (
                    <div key={stat.label}>
                      <dd className="font-heading text-2xl font-bold text-foreground-950">{stat.value}</dd>
                      <dt className="mt-1 text-[11px] text-foreground-500">{stat.label}</dt>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-6">
                <figure className="overflow-hidden rounded-lg border border-background-300">
                  <img
                    src="https://readdy.ai/api/search-image?query=warm%20editorial%20flatlay%20of%20an%20archivist%20desk%20with%20stacked%20paper%20folders%2C%20brass%20paper%20clips%2C%20a%20magnifier%20and%20a%20cup%20of%20tea%20on%20cream%20paper%2C%20muted%20forest%20green%20and%20ochre%20accents%2C%20soft%20daylight%2C%20calm%20top%20down%20composition&width=1000&height=760&seq=gt-about-01&orientation=landscape"
                    alt="瓜田黑料编辑桌上整理中的案例档案与放大镜"
                    title="瓜田黑料 - 案例档案整理"
                    className="h-64 w-full object-top md:h-96"
                  />
                </figure>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
                <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-600">
                  <p>
                    {brand.name}从 2021 年开始做同一件事：在一条信息被转了几万次之后，把它重新摊平在桌面上，
                    按时间排好，再问问每一段材料的来路。我们不生产爆料，也不追第一时间。
                  </p>
                  <p>
                    每天都有大量的事件在极短时间内被定义、被定性、被盖棺。等到完整的材料出现，
                    舆论往往已经转移到下一个话题，没有人回头把顺序重新排一遍。我们补的就是这个位置。
                  </p>
                  <p>
                    站内所有案例都经过匿名化处理，我们关注的是信息如何流转、判断如何偏移，
                    而不是某个具体的人做错了什么。这也是我们不接受任何针对个人的爆料投递的原因。
                  </p>
                </div>
                <div className="mt-7 grid grid-cols-2 gap-5 border-t border-background-300 pt-6">
                  {homeStats.slice(2).map((stat) => (
                    <div key={stat.label}>
                      <p className="font-heading text-xl font-bold text-accent-600">{stat.value}</p>
                      <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <header className="max-w-3xl">
              <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">PRINCIPLES</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这四条写在我们的编辑规范第一条，任何一篇归档都要先满足它们。
              </p>
            </header>

            <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {principles.map((item, index) => (
                <li key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <span className="font-heading text-2xl font-bold text-background-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full border-b border-background-200 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <header className="max-w-3xl">
              <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">READERS</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这些话来自长期订阅简报的读者，经本人同意后匿名展示。
              </p>
            </header>

            <ul className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {testimonials.map((item) => (
                <li
                  key={item.name}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-400" aria-hidden="true" />
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  <div className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-xs font-bold text-primary-800">
                      {item.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">{item.name}</p>
                      <p className="text-[11px] text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contact" className="w-full scroll-mt-32 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">CONTACT</p>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  纠正、补充、合作或只是想聊聊某个案例，都可以直接发信。我们会逐封阅读，
                  但无法保证每封都及时回复，请见谅。
                </p>

                <dl className="mt-7 flex flex-col gap-5 border-t border-background-300 pt-6">
                  <div>
                    <dt className="text-[11px] tracking-wide text-foreground-500">通用联系邮箱</dt>
                    <dd className="mt-1 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                      <i className="ri-mail-line text-base text-primary-700" aria-hidden="true" />
                      {brand.email}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] tracking-wide text-foreground-500">线索专用邮箱</dt>
                    <dd className="mt-1 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                      <i className="ri-shield-keyhole-line text-base text-accent-600" aria-hidden="true" />
                      {brand.clueEmail}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] tracking-wide text-foreground-500">更新节奏</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-foreground-700">
                      案例库每日维护，观察简报每两周发出一次。
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="lg:col-span-7">
                <SubmitForm fieldPrefix="about" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollTools />
    </div>
  );
}